import { showToast } from './showToast';
import i18n from "../i18n/index.js";
import { apiBodyMessage } from './apiErrorMessage';

/**
 * Clase centralizada para el manejo de errores de la aplicación
 */
class ErrorHandler {
  constructor() {
    // Guard: cuando expira la sesión, varias requests en paralelo devuelven 401
    // a la vez. Sin esto, cada una mostraba el toast + logout + redirect (salían
    // ~6 toasts). Solo la primera se procesa; el resto se ignora. El flag no se
    // resetea porque el redirect hace recarga completa (window.location.replace).
    this._sessionExpiredHandled = false;
  }

  /**
   * Maneja la expiración de sesión (401) una sola vez, sin importar cuántas
   * requests fallen simultáneamente.
   * @param {Error} error - El objeto de error 401
   * @param {Function} onExpire - Acción de cierre (logout + redirect)
   * @param {string} [message] - Caller-supplied message, already translated
   *   (e.g. "Tu sesión expiró por inactividad"). Empty → default text.
   */
  handleSessionExpired(error, onExpire, message = null) {
    if (this._sessionExpiredHandled) return;
    this._sessionExpiredHandled = true;

    const customMessage =
      typeof message === "string" && message.trim()
        ? message
        : i18n.t("Tu sesión ha expirado. Por favor inicia sesión nuevamente.");

    this.handleApiError(error, {
      showNotification: true,
      customMessage,
      context: "Autenticación",
    });

    if (typeof onExpire === "function") onExpire();
  }

  /**
   * Message for a status branch: `message` may arrive as a string (used as is)
   * or as a Nest ValidationPipe array (joined with ' · '). Anything else falls
   * back to the per-status text.
   * @param {Object} data - response body
   * @param {string} fallbackText - translated text for the status
   * @returns {string}
   */
  _statusMessage(data, fallbackText) {
    const message = data?.message;
    if (Array.isArray(message)) {
      const parts = message
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => item.trim());
      return parts.length ? parts.join(' · ') : fallbackText;
    }
    if (typeof message === 'string' && message) return message;
    return fallbackText;
  }

  /**
   * Maneja errores de respuestas HTTP
   * @param {Error} error - El objeto de error
   * @param {Object} options - Opciones de configuración
   * @param {string} [options.fallbackMessage] - i18n key used when the backend
   *   sends no text of its own (any status, 403/429/5xx included; loses to
   *   customMessage and does not apply to CORS/network errors)
   * @returns {Object} - Objeto con información del error
   */
  handleApiError(error, options = {}) {
    const {
      showNotification = true,
      customMessage = null,
      fallbackMessage = null,
      logError = true,
      context = 'API Call'
    } = options;

    let errorInfo = {
      message: i18n.t('Ha ocurrido un error inesperado'),
      status: null,
      data: null,
      originalError: error
    };

    // Detectar errores de CORS (tiene prioridad sobre otros mensajes)
    const isCorsError = this.isCorsError(error);
    
    if (isCorsError) {
      errorInfo.message = i18n.t('Error de CORS: No se puede conectar con el servidor. Por favor, recarga la página e intenta nuevamente.');
      errorInfo.status = 'CORS_ERROR';
    } else if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      const { status, data } = error.response;
      errorInfo.status = status;
      errorInfo.data = data;

      switch (status) {
        case 400:
          errorInfo.message = this._statusMessage(data, i18n.t('Solicitud incorrecta. Por favor verifica los datos enviados.'));
          break;
        case 401:
          errorInfo.message = this._statusMessage(data, i18n.t('Credenciales incorrectas. Por favor verifica tu email y contraseña.'));
          break;
        case 403:
          errorInfo.message = i18n.t('No tienes permisos para realizar esta acción.');
          break;
        case 404:
          errorInfo.message = this._statusMessage(data, i18n.t('Recurso no encontrado.'));
          break;
        case 409:
          errorInfo.message = this._statusMessage(data, i18n.t('Conflicto con el recurso existente.'));
          break;
        case 422:
          errorInfo.message = this._statusMessage(data, i18n.t('Error de validación. Verifica los datos ingresados.'));
          break;
        case 429:
          errorInfo.message = i18n.t('Demasiadas solicitudes. Por favor intenta más tarde.');
          break;
        case 500:
          errorInfo.message = i18n.t('Error interno del servidor. Por favor intenta más tarde.');
          break;
        case 502:
          errorInfo.message = i18n.t('Servicio no disponible temporalmente.');
          break;
        case 503:
          errorInfo.message = i18n.t('Servicio en mantenimiento. Por favor intenta más tarde.');
          break;
        default:
          errorInfo.message = this._statusMessage(data, i18n.t("Error del servidor: {{status}}", { status }));
      }
    } else if (error.request) {
      // La solicitud fue hecha pero no se recibió respuesta
      errorInfo.message = i18n.t('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
    } else {
      // Algo sucedió al configurar la solicitud
      errorInfo.message = error.message || i18n.t('Error al procesar la solicitud.');
    }

    // Opt-in: the backend text wins over the per-status text. Only with a real
    // response (without one, the connection/CORS text above stands).
    if (!customMessage && fallbackMessage && error.response && !isCorsError) {
      errorInfo.message = apiBodyMessage(error.response.data) ?? i18n.t(fallbackMessage);
    }

    // Usar mensaje personalizado si se proporciona, pero NO si es un error de CORS
    // (el mensaje de CORS ya fue establecido arriba y tiene prioridad)
    if (customMessage && !isCorsError) {
      errorInfo.message = customMessage;
    }

    // Mostrar notificación si está habilitado
    if (showNotification) {
      showToast(errorInfo.message, false);
    }

    // Registrar el error en consola si está habilitado
    if (logError) {
      console.error(`[${context}] Error:`, {
        message: errorInfo.message,
        status: errorInfo.status,
        data: errorInfo.data,
        originalError: error
      });
    }

    return errorInfo;
  }

  /**
   * Wrapper para llamadas API con manejo de errores automático
   * @param {Function} apiCall - Función que realiza la llamada API
   * @param {Object} options - Opciones de configuración
   * @returns {Promise} - Promesa con el resultado o error
   */
  async executeApiCall(apiCall, options = {}) {
    const {
      showNotification = true,
      customMessage = null,
      fallbackMessage = null,
      logError = true,
      context = 'API Call',
      onSuccess = null,
      onError = null
    } = options;

    try {
      const response = await apiCall();
      
      // Ejecutar callback de éxito si existe
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(response);
      }

      return {
        success: true,
        data: response.data || response,
        error: null
      };
    } catch (error) {
      const errorInfo = this.handleApiError(error, {
        showNotification,
        customMessage,
        fallbackMessage,
        logError,
        context
      });

      // Ejecutar callback de error si existe
      if (onError && typeof onError === 'function') {
        onError(errorInfo);
      }

      return {
        success: false,
        data: null,
        error: errorInfo
      };
    }
  }

  /**
   * Maneja múltiples llamadas API en paralelo
   * @param {Array} apiCalls - Array de funciones de llamadas API
   * @param {Object} options - Opciones de configuración
   * @returns {Promise} - Promesa con los resultados
   */
  async executeMultipleApiCalls(apiCalls, options = {}) {
    const results = await Promise.allSettled(
      apiCalls.map(call => this.executeApiCall(call, options))
    );

    return {
      success: results.every(r => r.status === 'fulfilled' && r.value.success),
      results: results.map(r => r.status === 'fulfilled' ? r.value : { success: false, error: r.reason })
    };
  }

  /**
   * Valida la respuesta de la API
   * @param {Object} response - Respuesta de la API
   * @returns {boolean} - true si la respuesta es válida
   */
  validateApiResponse(response) {
    return response && !response.error && response.data !== undefined;
  }

  /**
   * Detecta si un error es un error de CORS
   * @param {Error} error - El objeto de error
   * @returns {boolean} - true si es un error de CORS
   */
  isCorsError(error) {
    // Los errores de CORS generalmente:
    // 1. No tienen error.response (el navegador bloquea antes de recibir respuesta)
    // 2. Tienen error.request pero el mensaje contiene "CORS" o "cross-origin"
    // 3. El código de estado es 0 o undefined
    // 4. El mensaje puede contener "Network Error" o "CORS"
    
    if (!error) return false;
    
    const errorMessage = (error.message || '').toLowerCase();
    const hasCorsKeywords = errorMessage.includes('cors') || 
                           errorMessage.includes('cross-origin') ||
                           errorMessage.includes('network error');
    
    // Si no hay respuesta pero hay request, y el mensaje contiene palabras clave de CORS
    if (!error.response && error.request && hasCorsKeywords) {
      return true;
    }
    
    // Si el código de estado es 0 (común en errores de CORS)
    if (error.response && error.response.status === 0) {
      return true;
    }
    
    // Si el mensaje específicamente menciona CORS
    if (hasCorsKeywords) {
      return true;
    }
    
    return false;
  }

  /**
   * Extrae el mensaje de error de diferentes formatos de respuesta
   * @param {Object} error - Objeto de error
   * @returns {string} - Mensaje de error
   */
  extractErrorMessage(error) {
    if (typeof error === 'string') return error;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    if (error?.error?.message) return error.error.message;
    return i18n.t('Ha ocurrido un error inesperado');
  }
}

// Exportar una instancia singleton
export const errorHandler = new ErrorHandler();

// Exportar la clase para casos donde se necesite crear instancias personalizadas
export default ErrorHandler;

